import React, { useState } from 'react';
import { createShareKey, generateShareURL } from '../../services/shareService';
import { useAuth } from '../../contexts/AuthContext';
import './PasswordShareModal.css';

/**
 * 패스워드 공유 모달 컴포넌트
 * 간단하고 안전한 패스워드 공유 설정 UI 제공
 */
function PasswordShareModal({ password, isOpen, onClose }) {
  const { user } = useAuth();
  
  // 사용자 로그인 체크
  if (!user) {
    return null;
  }
  
  // 공유 설정 상태
  const [shareSettings, setShareSettings] = useState({
    expirationHours: 24,
    maxUses: 5
  });
  
  // UI 상태
  const [isLoading, setIsLoading] = useState(false);
  const [shareKey, setShareKey] = useState(null);
  const [shareURL, setShareURL] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('settings'); // 'settings' | 'generated'

  // 모달이 열릴 때 상태 초기화
  React.useEffect(() => {
    if (isOpen) {
      setShareKey(null);
      setShareURL(null);
      setError(null);
      setStep('settings');
      setShareSettings({
        expirationHours: 24,
        maxUses: 5
      });
    }
  }, [isOpen]);

  // 유효기간 옵션
  const expirationOptions = [
    { value: 1, label: '1시간' },
    { value: 3, label: '3시간' },
    { value: 6, label: '6시간' },
    { value: 12, label: '12시간' },
    { value: 24, label: '1일' },
    { value: 72, label: '3일' },
    { value: 168, label: '1주일' }
  ];

  // 최대 사용 횟수 옵션
  const maxUsesOptions = [1, 2, 3, 5, 10];

  // 공유키 생성 핸들러
  const handleCreateShare = async () => {
    if (!user || !password) {
      setError('로그인이 필요하거나 패스워드 정보가 없습니다.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 공유할 패스워드 데이터 준비
      const shareData = {
        title: password.title,
        username: password.username,
        password: password.password,
        website: password.website || null
      };

      // 공유키 생성 (사용자 정보 포함)
      const newShareKey = await createShareKey(user.uid, shareData, {
        ...shareSettings,
        userName: user.displayName || user.email || '알 수 없음',
        userEmail: user.email || ''
      });
      
      // 공유 URL 생성
      const newShareURL = generateShareURL(newShareKey);
      
      setShareKey(newShareKey);
      setShareURL(newShareURL);
      setStep('generated');
      
    } catch (error) {
      console.error('공유키 생성 실패:', error);
      
      // 권한 오류나 fallback은 실제로 성공적인 상황 (레거시 암호화 사용)
      if (error.message?.includes('permissions') || error.message?.includes('FALLBACK_TO_LEGACY')) {
        // 이 경우는 실제로는 성공이므로 에러로 처리하지 않음
        console.info('📱 모바일에서 안전한 레거시 암호화 방식을 사용했습니다.');
        // shareKey와 shareURL이 이미 설정되었을 것이므로 step을 계속 진행
        if (shareKey && shareURL) {
          setStep('generated');
        } else {
          setError('공유키 생성에 실패했습니다. 다시 시도해주세요.');
        }
      } else {
        setError(error.message || '공유키 생성에 실패했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 공유 URL 복사
  const handleCopyURL = async () => {
    if (!shareURL) return;

    try {
      await navigator.clipboard.writeText(shareURL);
      alert('공유 링크가 클립보드에 복사되었습니다!');
    } catch (error) {
      console.error('복사 실패:', error);
      
      // 대체 복사 방법
      const textArea = document.createElement('textarea');
      textArea.value = shareURL;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('공유 링크가 복사되었습니다!');
    }
  };

  // 새 공유키 생성 (다시 설정으로 돌아가기)
  const handleCreateNew = () => {
    setStep('settings');
    setShareKey(null);
    setShareURL(null);
    setError(null);
  };

  // 모달이 닫혀있으면 렌더링하지 않음
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="share-modal-content" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>🔗 패스워드 공유</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* 공유할 패스워드 정보 */}
          <div className="share-password-info">
            <h4>📋 공유할 패스워드</h4>
            <div className="password-preview">
              <div><strong>제목:</strong> {password?.title}</div>
              <div><strong>사용자명:</strong> {password?.username}</div>
              {password?.website && (
                <div><strong>웹사이트:</strong> {password.website}</div>
              )}
            </div>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {/* 단계별 UI */}
          {step === 'settings' && (
            <div className="share-settings">
              <h4>⚙️ 공유 설정</h4>
              
              {/* 유효기간 설정 */}
              <div className="setting-group">
                <label>🕐 유효기간</label>
                <select
                  value={shareSettings.expirationHours}
                  onChange={(e) => setShareSettings({
                    ...shareSettings,
                    expirationHours: parseInt(e.target.value)
                  })}
                  disabled={isLoading}
                >
                  {expirationOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small>공유 링크가 자동으로 만료되는 시간입니다.</small>
              </div>

              {/* 최대 사용 횟수 설정 */}
              <div className="setting-group">
                <label>🔢 최대 사용 횟수</label>
                <select
                  value={shareSettings.maxUses}
                  onChange={(e) => setShareSettings({
                    ...shareSettings,
                    maxUses: parseInt(e.target.value)
                  })}
                  disabled={isLoading}
                >
                  {maxUsesOptions.map(count => (
                    <option key={count} value={count}>
                      {count}회
                    </option>
                  ))}
                </select>
                <small>공유 링크를 사용할 수 있는 최대 횟수입니다.</small>
              </div>

              {/* 보안 안내 */}
              <div className="security-notice">
                <h5>🔐 보안 안내</h5>
                <ul>
                  <li>공유 링크는 설정한 시간이 지나면 자동으로 만료됩니다</li>
                  <li>설정한 횟수만큼 사용하면 더 이상 접근할 수 없습니다</li>
                  <li>언제든지 공유를 취소할 수 있습니다</li>
                  <li>신뢰할 수 있는 사람에게만 공유하세요</li>
                </ul>
              </div>
            </div>
          )}

          {step === 'generated' && (
            <div className="share-result">
              <h4>✅ 공유 링크 생성 완료</h4>
              
              {/* 공유 정보 */}
              <div className="share-info">
                <div className="share-settings-summary">
                  <strong>설정:</strong> {shareSettings.expirationHours}시간 후 만료, 최대 {shareSettings.maxUses}회 사용
                </div>
              </div>

              {/* 공유 URL */}
              <div className="share-url-container">
                <label>🔗 공유 링크</label>
                <div className="share-url-box">
                  <input
                    type="text"
                    value={shareURL}
                    readOnly
                    className="share-url-input"
                  />
                  <button
                    onClick={handleCopyURL}
                    className="btn-copy-url"
                    title="링크 복사"
                  >
                    📋 복사
                  </button>
                </div>
                <small>전체 링크를 복사해서 공유하세요.</small>
              </div>

              {/* 액세스 코드 */}
              <div className="access-code-container">
                <label>🔑 액세스 코드</label>
                <div className="access-code-info">
                  <p>받는 사람이 수동으로 입력하려면 아래 코드를 사용하세요:</p>
                </div>
                <div className="access-code-box">
                  <input
                    type="text"
                    value={shareKey}
                    readOnly
                    className="access-code-input"
                  />
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(shareKey);
                        alert('액세스 코드가 클립보드에 복사되었습니다!');
                      } catch (error) {
                        const textArea = document.createElement('textarea');
                        textArea.value = shareKey;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        alert('액세스 코드가 복사되었습니다!');
                      }
                    }}
                    className="btn-copy-code"
                    title="코드 복사"
                  >
                    📋 복사
                  </button>
                </div>
                <div className="access-code-usage">
                  <h5>💡 액세스 코드 사용법</h5>
                  <ol>
                    <li>PW-Wallet에 로그인</li>
                    <li>"공유받은 패스워드" 메뉴 선택</li>
                    <li>"액세스 코드 입력" 버튼 클릭</li>
                    <li>위 코드 입력 후 확인</li>
                  </ol>
                </div>
              </div>

              {/* 주의사항 */}
              <div className="share-warning">
                <p>⚠️ 이 링크나 코드를 받은 사람은 패스워드를 볼 수 있습니다.</p>
                <p>🔐 안전한 방법(메신저, 이메일 등)으로만 공유하세요.</p>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 'settings' && (
            <>
              <button onClick={onClose} className="btn-cancel">
                취소
              </button>
              <button
                onClick={handleCreateShare}
                disabled={isLoading}
                className="btn-primary"
              >
                {isLoading ? '생성 중...' : '🔗 공유 링크 생성'}
              </button>
            </>
          )}

          {step === 'generated' && (
            <>
              <button onClick={handleCreateNew} className="btn-secondary">
                새 링크 생성
              </button>
              <button onClick={onClose} className="btn-primary">
                완료
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PasswordShareModal;